import {MapDefaultContextMenuComponent} from '@geogirafe/lib-geoportal/components';
import proj4 from 'proj4';
import {v4 as uuidv4} from 'uuid';

class SitnContextMenuComponent extends MapDefaultContextMenuComponent {
  override templateUrl = './template.html';
  override styleUrls = [
    './style.css',
    '../../../node_modules/@geogirafe/lib-geoportal/components/context-menu/mapcontextmenu.css',
    '../../../node_modules/@geogirafe/lib-geoportal/components/context-menu/default-context-menu/style.css',
    '../../../node_modules/ol-ext/overlay/Popup.css',
  ];
  proj4 = proj4;

  addressData = {};
  addressLoading = false;

  rasterData = {};
  rasterLoading = false;

  getElementText(id: string): string {
    return this.host.querySelector(id)!.textContent!.trim();
  }

  decimalToDMS(coordinate: number[]): string[] {
    const convertToDMS = (decimal: number, isLatitude: boolean): string => {
      const degrees = Math.floor(Math.abs(decimal));
      const minutes = Math.floor((Math.abs(decimal) - degrees) * 60);
      const seconds = Math.round((Math.abs(decimal) - degrees - minutes / 60) * 3600 * 100) / 100;
      let direction;
      if (isLatitude) {
        direction = decimal >= 0 ? 'N' : 'S';
      } else {
        direction = decimal >= 0 ? 'E' : 'W';
      }
      return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
    };

    const latitude = coordinate[1];
    const longitude = coordinate[0];
    return [convertToDMS(longitude, false), convertToDMS(latitude, true)];
  }

  async getAddressData(): Promise<void> {
    const addressUrl = (this.context.configManager.Config.extendedConfig!['contextmenu'] as any)[
      'addressURL'
    ];
    this.addressLoading = true;
    this.addressData = {};
    super.renderContent();
    const url = this.replaceCoordinateString(addressUrl, 'EPSG:2056', 2, 'decimal');

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
      const result = await response.json();

      if (result.features.length > 0) {
        this.addressData = result.features[0].properties;
      }

      this.addressLoading = false;
      super.renderContent();
    } catch (error) {
      console.error('Error trying to retrieve address data');
    }
  }

  async getRasterData(): Promise<void> {
    const rasterURL = (this.context.configManager.Config.extendedConfig!['contextmenu'] as any)['rasterURL'];
    this.rasterLoading = true;
    this.rasterData = {};
    super.renderContent();
    const url = this.replaceCoordinateString(rasterURL, 'EPSG:2056', 2, 'decimal');

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
      const result = await response.json();
      this.rasterData = result;
      this.rasterLoading = false;
      super.renderContent();
    } catch (error) {
      console.error('Error trying to retrieve raster data');
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.state.infobox.elements.push({
      id: uuidv4(),
      text: this.context.i18nManager.getTranslation('copy-validation'),
      type: 'info',
      duration: 3000,
    });
  }

  override showContextMenu(): void {
    super.showContextMenu();
    this.getAddressData();
    this.getRasterData();
  }

  replaceCoordinateString(
    string: string,
    targetCRS: string,
    precision: number,
    format: string = 'decimal',
  ): string {
    if (!this.mapContextMenuState.projection || !this.mapContextMenuState.position) {
      throw new Error('Missing projection or position in MapContextMenuState');
    }

    const coord = this.proj4(
      this.mapContextMenuState.projection,
      targetCRS,
      this.mapContextMenuState.position,
    );
    let coordstring: string[];

    switch (format) {
      case 'dms':
        coordstring = this.decimalToDMS(coord);
        break;
      case 'decimal':
        coordstring = coord.map((v) => v.toFixed(precision));
        break;
      default:
        coordstring = coord.map((v) => v.toFixed(precision));
    }

    return string.replace(/###MAPX###/g, coordstring[0]).replace(/###MAPY###/g, coordstring[1]);
  }
}

export default SitnContextMenuComponent;
